/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-03-01 15:00:00
 *
 *******************************************************/

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Card, Icon, FormLayout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Field, reduxForm } from 'redux-form';
import { routes } from 'src/routes';
import ButtonCustom from './button';
import history from 'src/routes/history';
import * as installActions from 'actions/install-actions';
import * as commonAction from 'src/stores/actions/common-actions.js';
import { DATA_ONBOARDING } from 'src/constants/installation-v2';
import { getShop } from 'src/helpers';
import { ChevronLeftMinor, ChevronRightMinor } from '@shopify/polaris-icons';
import { checkUserShowEmailOpt } from 'src/helpers/installation-helper';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { isEmailOpt } from 'src/helpers/form-validation-helper';
import * as optionEmailAction from 'actions/dashboard/option-email-action';

const isEmailOptValue = isEmailOpt();

const NUM_SLIDES = 5;
type Props = WithI18nProps;

const Step = ({ length, activeIndex, onClickStep }) => {
  return (
    <div className="Step">
      {[...Array(length)].map((i, index) => {
        return (
          <div
            key={index}
            className={`item ${index === activeIndex ? 'active' : ''}`}
            onClick={() => onClickStep(index)}></div>
        );
      })}
    </div>
  );
};

const ScreenRender = ({
  title,
  description,
  image,
  value,
  currentStepIndex,
  onBack,
  onNext,
  onLetgo,
  onClickStep,
  i18n,
  isNewUser
}) => (
  <Card>
    <div className={`BC-page_Installation--Onboarding ${isNewUser ? 'NewUser' : ''}`}>
      <div className="wrapper">
        <div className="btn-pre" onClick={onBack}>
          <Icon source={ChevronLeftMinor} color="base" />
        </div>
        <div className="btn-next" onClick={() => onNext(value)}>
          <Icon source={ChevronRightMinor} color="base" />
        </div>
        <div className="preview">
          <img src={image} alt="" width={560} height={645} />
        </div>
        <div className="content">
          <div className="title">{i18n.translate(title, { br: <br /> })}</div>
          <div className={`description step-${currentStepIndex}`}>
            {i18n.translate(description, { br: <br /> })}
          </div>
          <div className="step">
            <Step length={NUM_SLIDES} activeIndex={currentStepIndex} onClickStep={onClickStep} />
          </div>
          <div className="menu">
            <div className="main-btn">
              <ButtonCustom onClick={() => onLetgo()}>
                {i18n.translate('AdminHTML.InstallationV2.Onboarding.step1_btn')}
              </ButtonCustom>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Card>
);

const EmailOptScreen = ({ i18n, handleSubmit, addOptionEmail, sendOptEmailLoading }) => {
  // Handle default form submit
  const handleDefaultSubmit = e => {
    console.log('handleDefaultSubmit: ', { e });
    e.preventDefault();
  };

  const handleSave = data => {
    addOptionEmail({
      ...data,
      isOnboard: true
    });
  };

  return (
    <Card>
      <div className={`BC-page_Installation--Onboarding NewUser`}>
        <div className="wrapper">
          <div className="preview">
            <img src="/assets/images/installation/email.svg" alt="" width={560} height={645} />
          </div>
          <div className="content form-email-opt">
            <div className="title">
              {i18n.translate('AdminHTML.InstallationV2.Onboarding.emailopt_title')}
            </div>
            <form onSubmit={handleDefaultSubmit}>
              <FormLayout>
                <Field
                  name="optEmail"
                  label={i18n.translate('AdminHTML.InstallationV2.Onboarding.emailopt_label')}
                  component={RenderTextField}
                  placeholder={i18n.translate('AdminHTML.InstallationV2.Onboarding.emailopt_hint')}
                  validate={[isEmailOptValue]}
                />
              </FormLayout>
              <div>
                {i18n.translate('AdminHTML.InstallationV2.Onboarding.emailopt_description')}
              </div>
              <div>
                {i18n.translate('AdminHTML.InstallationV2.Onboarding.emailopt_description2')}
              </div>
            </form>
            <div className="menu">
              <div className="main-btn">
                <ButtonCustom
                  onClick={handleSubmit(handleSave.bind(this))}
                  loading={sendOptEmailLoading}
                  isDisabled={sendOptEmailLoading}>
                  {i18n.translate('AdminHTML.InstallationV2.Onboarding.emailopt_btn')}
                </ButtonCustom>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const EmailOptForm = reduxForm({
  form: 'EmailOptForm', // a unique identifier for this form
  enableReinitialize: true
})(EmailOptScreen);

class OnboardingScreen extends Component<Props> {
  state = {
    isSourceFromSelectOther: false,
    currentStepIndex: 0,
    intervalId: null
  };

  componentDidMount() {
    const { installStatus, enableNewFeatureOnboarding } = this.props;
    const intervalId = setInterval(this.onNext, 2000);
    const step = installStatus.split('submitted-story-telling-')[1];
    const currentStepIndex = +step || 0;
    this.setState({
      currentStepIndex,
      intervalId
    });

    try {
      if (currentStepIndex === 0) {
        localStorage.removeItem(`${getShop()}--isTriggeredSlowSync`);
      }
      localStorage.setItem(`${getShop()}-isFirstTimeAccess`, true);
    } catch (error) {}

    enableNewFeatureOnboarding(true, 'not-finish-old-onboarding', '');
  }

  componentWillUnmount() {
    clearInterval(this.state.intervalId);
  }

  onBack = () => {
    const { currentStepIndex } = this.state;
    const stepIndex = currentStepIndex === 0 ? NUM_SLIDES - 1 : currentStepIndex - 1;
    this.setState({
      currentStepIndex: stepIndex
    });
  };

  onNext = () => {
    // 6 steps: 0 -> 5
    const { optEmail } = this.props.tenantApp;
    if (optEmail) {
      const { currentStepIndex } = this.state;
      const nextStep = currentStepIndex >= NUM_SLIDES - 1 ? 0 : currentStepIndex + 1;
      this.setState({ currentStepIndex: nextStep });
    }
  };

  onLetgo = async () => {
    const { updateInstallationStatus, tenantApp } = this.props;
    const isOldUser = tenantApp.firstInstalledAt !== tenantApp.recentInstalledAt;
    const isNewUser = enableShopifyIntegration(tenantApp);

    if (isNewUser) {
      if (isOldUser) {
        updateInstallationStatus('submitted-kyc');
        history.push(routes.newInstallation.successInstall());
      } else {
        updateInstallationStatus('submitted-onboarding');
        history.push(routes.newInstallation.kyc());
      }
    } else {
      if (isOldUser) {
        updateInstallationStatus('submitted-kyc');
        history.push(routes.newInstallation.chooseTheme());
      } else {
        updateInstallationStatus('submitted-onboarding');
        history.push(routes.newInstallation.kyc());
      }
    }
  };

  onClickStep = step => {
    this.setState({ currentStepIndex: step });
  };

  render() {
    const { currentStepIndex } = this.state;
    const { i18n, tenantApp, addOptionEmail, sendOptEmailLoading, sendOptEmailSuccess } =
      this.props;
    const { title, description, image, value } = DATA_ONBOARDING[currentStepIndex];
    const isNewUser = enableShopifyIntegration(tenantApp);
    const isNewUserAndNotHaveOptEmail = checkUserShowEmailOpt(tenantApp);

    if (isNewUserAndNotHaveOptEmail && !sendOptEmailSuccess) {
      return (
        <div className="BC-AnotherLayout BC-page_Installation Onboarding">
          <EmailOptForm
            addOptionEmail={addOptionEmail}
            sendOptEmailLoading={sendOptEmailLoading}
            i18n={i18n}></EmailOptForm>
        </div>
      );
    }

    return (
      <div className="BC-AnotherLayout BC-page_Installation Onboarding">
        <ScreenRender
          title={title}
          description={description}
          image={image}
          value={value}
          currentStepIndex={currentStepIndex}
          onBack={this.onBack}
          onClickStep={this.onClickStep}
          onNext={this.onNext}
          onLetgo={this.onLetgo}
          isNewUser={isNewUser}
          i18n={i18n}></ScreenRender>
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  return {
    tenantApp: _.get(state.oauth, 'tenantApp', {}),
    loading: state.install.loading,
    success: state.install.success,
    error: state.install.error,
    installStatus: _.get(state, 'oauth.tenantApp.installStatus'),
    sendOptEmailLoading: _.get(state, 'optionEmail.loading'),
    sendOptEmailSuccess: _.get(state, 'optionEmail.success')
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  addOptionEmail: (body: Object) => dispatch(optionEmailAction.addOptionEmail(body, ownProps.i18n)),
  updateInstallationStatus: status => dispatch(installActions.updateInstallationStatus(status)),
  enableNewFeatureOnboarding: (enable, source, action) =>
    dispatch(commonAction.enableNewFeatureOnboarding(enable, source, action, ownProps))
});

OnboardingScreen = reduxForm({
  form: 'OnboardingScreenForm',
  enableReinitialize: true
})(OnboardingScreen);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(OnboardingScreen));
